export const IDENTITY_STATUS = Object.freeze({
  PROVEN: 'PROVEN',
  UNKNOWN: 'UNKNOWN',
  CONFLICT: 'CONFLICT',
  OBSOLETE: 'OBSOLETE',
});

function text(value) {
  return String(value ?? '').trim();
}

export function normalizeSerial(value) {
  const normalized = text(value).toUpperCase().replace(/\s+/g, '');
  return normalized || null;
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

function serialValues(record = {}) {
  return unique([
    ...(Array.isArray(record.serialAliases) ? record.serialAliases.map(normalizeSerial) : []),
    normalizeSerial(record.serialNumber),
    normalizeSerial(record.serial),
    normalizeSerial(record.robotSerial),
    normalizeSerial(record.belroboticsId),
    normalizeSerial(record.belroboticsID),
  ]);
}

function robotIdValues(record = {}) {
  return unique([
    ...(Array.isArray(record.robotIdAliases) ? record.robotIdAliases.map(text) : []),
    text(record.robotId), text(record.robotID), text(record.sourceRobotId), text(record.myRobotId),
  ]);
}

function documentIdValues(record = {}) {
  return unique([
    ...(Array.isArray(record.documentIdAliases) ? record.documentIdAliases.map(text) : []),
    text(record.docId), text(record.documentId), text(record.robotDocumentId), text(record.robotDocId),
  ]);
}

export function buildIdentityCandidate(record = {}, key = '') {
  const serials = serialValues(record);
  const robotIds = robotIdValues(record);
  const documentIds = documentIdValues(record);
  return Object.freeze({
    key,
    serialNumber: serials.length === 1 ? serials[0] : null,
    robotId: robotIds.length === 1 ? robotIds[0] : null,
    documentId: documentIds.length === 1 ? documentIds[0] : null,
    displayName: text(record.name || record.naam || record.robotName) || null,
    identityStatus: serials.length > 1 || robotIds.length > 1 || documentIds.length > 1
      ? IDENTITY_STATUS.CONFLICT
      : IDENTITY_STATUS.UNKNOWN,
  });
}

export function buildIdentityCatalog(records = []) {
  return finalizeIdentityCatalog(records.map(({ key, data }) => {
    const candidate = buildIdentityCandidate({
      ...data,
      docId: data?.docId || data?.documentId || data?.robotDocumentId || data?.id || null,
    }, key);
    const identityKey = candidate.serialNumber
      ? `serial:${candidate.serialNumber}`
      : candidate.robotId
        ? `robot:${candidate.robotId}`
        : candidate.documentId
          ? `document:${candidate.documentId}`
        : `record:${key}`;
    return Object.freeze({ ...data, ...candidate, key: identityKey });
  }));
}

export function finalizeIdentityCatalog(catalog = []) {
  const serialCounts = new Map();
  const robotIdCounts = new Map();
  const documentIdCounts = new Map();
  catalog.forEach((robot) => {
    if (robot.serialNumber) serialCounts.set(robot.serialNumber, (serialCounts.get(robot.serialNumber) || 0) + 1);
    if (robot.robotId) robotIdCounts.set(robot.robotId, (robotIdCounts.get(robot.robotId) || 0) + 1);
    if (robot.documentId) documentIdCounts.set(robot.documentId, (documentIdCounts.get(robot.documentId) || 0) + 1);
  });
  return Object.freeze(catalog.map((robot) => {
    const serialConflict = robot.serialNumber && serialCounts.get(robot.serialNumber) > 1;
    const robotIdConflict = robot.robotId && robotIdCounts.get(robot.robotId) > 1;
    const documentIdConflict = robot.documentId && documentIdCounts.get(robot.documentId) > 1;
    const status = robot.identityStatus === IDENTITY_STATUS.CONFLICT || serialConflict || robotIdConflict || documentIdConflict
      ? IDENTITY_STATUS.CONFLICT
      : robot.serialNumber || robot.robotId || robot.documentId
        ? IDENTITY_STATUS.PROVEN
        : IDENTITY_STATUS.UNKNOWN;
    return Object.freeze({ ...robot, identityStatus: status });
  }));
}

export function resolveIdentity(record = {}, catalog = []) {
  const candidate = buildIdentityCandidate(record, 'event');
  if (candidate.identityStatus === IDENTITY_STATUS.CONFLICT) {
    return Object.freeze({ status: IDENTITY_STATUS.CONFLICT, identityKey: null, robot: null, reason: 'MULTIPLE_IDENTIFIERS_IN_RECORD' });
  }
  const matchGroups = [
    candidate.serialNumber ? catalog.filter((robot) => robot.serialNumber === candidate.serialNumber) : [],
    candidate.robotId ? catalog.filter((robot) => robot.robotId === candidate.robotId) : [],
    candidate.documentId ? catalog.filter((robot) => robot.documentId === candidate.documentId) : [],
  ].filter((matches) => matches.length > 0);
  if (matchGroups.some((matches) => matches.length > 1)) {
    return Object.freeze({ status: IDENTITY_STATUS.CONFLICT, identityKey: null, robot: null, reason: 'NON_UNIQUE_STABLE_IDENTIFIER' });
  }
  const matches = [...new Map(matchGroups.flat().map((robot) => [robot.key, robot])).values()];
  if (matches.length > 1) {
    return Object.freeze({ status: IDENTITY_STATUS.CONFLICT, identityKey: null, robot: null, reason: 'CONTRADICTORY_STABLE_IDENTIFIERS' });
  }
  if (matches.length === 1 && matches[0].identityStatus === IDENTITY_STATUS.PROVEN) {
    return Object.freeze({ status: IDENTITY_STATUS.PROVEN, identityKey: matches[0].key, robot: matches[0], reason: 'EXACT_UNIQUE_STABLE_IDENTIFIER' });
  }
  if (matches.length > 1 || matches.some((robot) => robot.identityStatus === IDENTITY_STATUS.CONFLICT)) {
    return Object.freeze({ status: IDENTITY_STATUS.CONFLICT, identityKey: null, robot: null, reason: 'NON_UNIQUE_STABLE_IDENTIFIER' });
  }
  return Object.freeze({
    status: candidate.serialNumber || candidate.robotId ? IDENTITY_STATUS.UNKNOWN : IDENTITY_STATUS.UNKNOWN,
    identityKey: null,
    robot: null,
    reason: candidate.serialNumber || candidate.robotId ? 'NO_CATALOG_MATCH' : 'NO_STABLE_IDENTIFIER',
  });
}

export function resolveRobotRecord(record, robots = []) {
  const catalog = buildIdentityCatalog(robots.map((robot, index) => ({
    key: String(robot.docId || robot.id || index),
    data: robot,
  })));
  return resolveIdentity(record, catalog);
}
